/**
 * /frontend/src/features/appointments/utils/bookingHistoryCsv.js
 *
 * Maps appointment-history booking responses to flat CSV rows for the
 * "Download history" feature. Money fields arrive in paise from the backend
 * and are rendered as plain rupee amounts.
 */

import { bookingServiceNames } from './bookingServices';
import { bookingStatusLabel } from './bookingStatus';

const pad = (value) => String(value).padStart(2, '0');

/** Splits an ISO / parseable datetime into local date + 24h time columns. */
const splitDateTime = (value) => {
  if (!value) return { date: '', time: '' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: String(value), time: '' };
  const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  return { date, time };
};

/** paise (integer) → rupee string, blank when absent or zero. */
const paiseToRupees = (paise) => {
  const numeric = Number(paise);
  if (!Number.isFinite(numeric) || numeric === 0) return '';
  const rupees = numeric / 100;
  return rupees % 1 === 0 ? String(rupees) : rupees.toFixed(2);
};

/** Gross bill for a booking = sum of each service's price at booking (in paise). */
const totalBillPaise = (booking) => {
  const services = Array.isArray(booking?.services) ? booking.services : [];
  return services.reduce((sum, service) => {
    const price = Number(service?.priceAtBookingPaise);
    return sum + (Number.isFinite(price) ? price : 0);
  }, 0);
};

/**
 * Turns the raw booking list into ordered CSV rows (array of objects). The key
 * order here is the column order in the exported file.
 * @param {Array<Record<string, unknown>>} bookings
 * @returns {Array<Record<string, string>>}
 */
export const bookingsToCsvRows = (bookings) => {
  const list = Array.isArray(bookings) ? bookings : [];
  return list.map((booking) => {
    const start = splitDateTime(booking?.appointmentStartTime);
    const end = splitDateTime(booking?.appointmentEndTime);
    const services = bookingServiceNames(booking).join('; ');
    return {
      Date: start.date,
      'Start time': start.time,
      'End time': end.time,
      Customer: booking?.customerName || '',
      Phone: booking?.customerPhone || '',
      Services: services,
      'Total bill (₹)': paiseToRupees(totalBillPaise(booking)),
      Status: bookingStatusLabel(booking?.status) || booking?.status || '',
      Type: booking?.walkin ? 'Walk-in' : 'Online',
      Offer: booking?.offerTitle || '',
      'Discount (₹)': paiseToRupees(booking?.discountPaise),
      'Membership benefit': booking?.membershipBenefitLabel || '',
      'Membership value (₹)': paiseToRupees(booking?.membershipBenefitPaise),
      'Loyalty reward': booking?.loyaltyRewardLabel || '',
      'Loyalty value (₹)': paiseToRupees(booking?.loyaltyRewardPaise),
      Notes: booking?.notes || '',
      'Booking ID': booking?.bookingUuid || ''
    };
  });
};

/** Builds the download filename for a given inclusive range. */
export const bookingHistoryFilename = (startDate, endDate) =>
  `appointment-history_${startDate}_to_${endDate}.csv`;
